import { useEffect, useRef, useState } from 'react';
import './App.css';

function App() {
  const canvasRef = useRef(null);
  const [showMessage, setShowMessage] = useState(false);
  const [started, setStarted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showSpecialWish, setShowSpecialWish] = useState(false);

  useEffect(() => {
    // 检测是否为移动设备
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
        || window.innerWidth < 768;
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const fireworks = [];
    const particles = [];

    // 根据设备调整性能参数
    const performanceConfig = {
      maxFireworks: isMobile ? 3 : 5,
      maxParticles: isMobile ? 1500 : 3000,
      particleCount: isMobile ? 80 : 150,
      starCount: isMobile ? 150 : 250,
      trailLength: isMobile ? 8 : 15,
      particleTrailLength: isMobile ? 5 : 8,
      fireworkInterval: isMobile ? 800 : 600,
    };

    // 超级惊艳的颜色方案
    const colorPalettes = [
      ['#ff0844', '#ffb199', '#ff9d00', '#ffd700'],
      ['#00d9ff', '#0099ff', '#8b5cf6', '#ec4899'],
      ['#ffd700', '#ffed4e', '#fff200', '#ff6b00'],
      ['#ff00ff', '#ff66ff', '#00ffff', '#ff99ff'],
      ['#00ff88', '#00ffcc', '#00ffff', '#0099ff'],
      ['#ff3366', '#ff6699', '#ffcc00', '#ff99cc'],
    ];

    // 烟花发射类
    class Firework {
      constructor(targetX, targetY) {
        this.x = targetX || (canvas.width * 0.2 + Math.random() * canvas.width * 0.6);
        this.startY = canvas.height;
        this.y = canvas.height;
        this.targetX = targetX || this.x;
        this.targetY = targetY || (canvas.height * 0.15 + Math.random() * canvas.height * 0.25);
        this.distanceToTarget = Math.sqrt(
          Math.pow(this.targetX - this.x, 2) + Math.pow(this.targetY - this.y, 2)
        );
        this.distanceTraveled = 0;
        this.palette = colorPalettes[Math.floor(Math.random() * colorPalettes.length)];
        this.mainColor = this.palette[0];
        this.trail = [];
        this.angle = Math.atan2(this.targetY - this.y, this.targetX - this.x);
        this.speed = 8;
        this.acceleration = 1.05;
      }

      update() {
        this.trail.push({ x: this.x, y: this.y });
        if (this.trail.length > performanceConfig.trailLength) this.trail.shift();

        const vx = Math.cos(this.angle) * this.speed;
        const vy = Math.sin(this.angle) * this.speed;
        
        this.distanceTraveled += Math.sqrt(vx * vx + vy * vy);
        
        if (this.distanceTraveled >= this.distanceToTarget) {
          this.explode();
          return false;
        }

        this.x += vx;
        this.y += vy;
        this.speed *= this.acceleration;

        return true;
      }

      draw() {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(this.trail[0]?.x || this.x, this.trail[0]?.y || this.y);
        this.trail.forEach((point) => {
          ctx.lineTo(point.x, point.y);
        });
        ctx.strokeStyle = this.mainColor;
        ctx.lineWidth = isMobile ? 2 : 3;
        ctx.shadowBlur = isMobile ? 10 : 20;
        ctx.shadowColor = this.mainColor;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(this.x, this.y, isMobile ? 3 : 4, 0, Math.PI * 2);
        ctx.fillStyle = '#fff';
        ctx.shadowBlur = isMobile ? 15 : 30;
        ctx.shadowColor = this.mainColor;
        ctx.fill();
        ctx.restore();
      }

      explode() {
        const particleCount = performanceConfig.particleCount + Math.floor(Math.random() * 50);
        const type = Math.random();
        
        if (type < 0.25) {
          this.createBurst(particleCount);
        } else if (type < 0.5) {
          this.createRing(particleCount);
        } else if (type < 0.75) {
          this.createSpiral(particleCount);
        } else {
          this.createGalaxy(particleCount);
        }
      }

      createBurst(count) {
        for (let i = 0; i < count; i++) {
          const angle = (Math.PI * 2 * i) / count;
          const speed = 3 + Math.random() * 8;
          const color = this.palette[Math.floor(Math.random() * this.palette.length)];
          particles.push(new Particle(this.targetX, this.targetY, angle, speed, color, 3));
        }
      }

      createRing(count) {
        for (let i = 0; i < count; i++) {
          const angle = (Math.PI * 2 * i) / count;
          const speed = 5 + Math.random() * 3;
          const color = this.palette[i % this.palette.length];
          particles.push(new Particle(this.targetX, this.targetY, angle, speed, color, 4));
        }
      }

      createSpiral(count) {
        for (let i = 0; i < count; i++) {
          const angle = (Math.PI * 4 * i) / count;
          const speed = 2 + (i / count) * 6;
          const color = this.palette[Math.floor((i / count) * this.palette.length)];
          particles.push(new Particle(this.targetX, this.targetY, angle, speed, color, 3));
        }
      }

      createGalaxy(count) {
        for (let i = 0; i < count * 0.6; i++) {
          const angle = (Math.PI * 2 * i) / (count * 0.6);
          const speed = 4 + Math.random() * 6;
          const color = this.palette[0];
          particles.push(new Particle(this.targetX, this.targetY, angle, speed, color, 3.5));
        }
        setTimeout(() => {
          for (let i = 0; i < count * 0.4; i++) {
            const angle = (Math.PI * 2 * i) / (count * 0.4);
            const speed = 2 + Math.random() * 4;
            const color = this.palette[this.palette.length - 1];
            particles.push(new Particle(this.targetX, this.targetY, angle, speed, color, 4));
          }
        }, 300);
      }
    }

    // 粒子类
    class Particle {
      constructor(x, y, angle, speed, color, size) {
        this.x = x;
        this.y = y;
        this.color = color;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.alpha = 1;
        this.decay = 0.008 + Math.random() * 0.007;
        this.gravity = 0.08;
        this.friction = 0.96;
        this.size = size || (2 + Math.random() * 2);
        this.trail = [];
      }

      update() {
        if (!isMobile || this.trail.length < performanceConfig.particleTrailLength) {
          this.trail.push({ x: this.x, y: this.y, alpha: this.alpha });
          if (this.trail.length > performanceConfig.particleTrailLength) this.trail.shift();
        }

        this.vx *= this.friction;
        this.vy *= this.friction;
        this.vy += this.gravity;
        this.x += this.vx;
        this.y += this.vy;
        this.alpha -= this.decay;
        
        return this.alpha > 0;
      }

      draw() {
        ctx.save();
        
        // 移动端简化拖尾
        if (!isMobile && this.trail.length > 1) {
          for (let i = 0; i < this.trail.length - 1; i++) {
            const t = this.trail[i];
            const alpha = (i / this.trail.length) * this.alpha * 0.6;
            ctx.globalAlpha = alpha;
            ctx.beginPath();
            ctx.arc(t.x, t.y, this.size * 0.6, 0, Math.PI * 2);
            ctx.fillStyle = this.color;
            ctx.fill();
          }
        }

        ctx.globalAlpha = this.alpha;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size);
        gradient.addColorStop(0, '#ffffff');
        gradient.addColorStop(0.3, this.color);
        gradient.addColorStop(1, this.color);
        ctx.fillStyle = gradient;
        
        ctx.shadowBlur = isMobile ? 15 : 25;
        ctx.shadowColor = this.color;
        ctx.fill();
        
        ctx.restore();
      }
    }

    // 星星
    const stars = [];
    for (let i = 0; i < performanceConfig.starCount; i++) {
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.5 + 0.5,
        alpha: Math.random() * 0.8 + 0.2,
        twinkleSpeed: Math.random() * 0.02 + 0.01
      });
    }

    let lastFireworkTime = 0;
    let animationId;

    // 动画循环
    function animate(timestamp) {
      ctx.fillStyle = 'rgba(5, 10, 25, 0.12)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 绘制星星
      stars.forEach(star => {
        ctx.fillStyle = `rgba(255, 255, 255, ${star.alpha})`;
        ctx.shadowBlur = star.size * 2;
        ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
        
        star.alpha += (Math.random() - 0.5) * star.twinkleSpeed;
        star.alpha = Math.max(0.2, Math.min(1, star.alpha));
      });
      ctx.shadowBlur = 0;

      // 只有启动后才自动发射烟花
      if (started && fireworks.length < performanceConfig.maxFireworks) {
        const timeSinceLastFirework = timestamp - lastFireworkTime;
        if (timeSinceLastFirework > performanceConfig.fireworkInterval || 
            (Math.random() < 0.08 && timeSinceLastFirework > performanceConfig.fireworkInterval * 0.7)) {
          fireworks.push(new Firework());
          lastFireworkTime = timestamp;
        }
      }

      // 更新和绘制烟花
      for (let i = fireworks.length - 1; i >= 0; i--) {
        if (!fireworks[i].update()) {
          fireworks.splice(i, 1);
        } else {
          fireworks[i].draw();
        }
      }

      // 粒子数量限制
      if (particles.length > performanceConfig.maxParticles) {
        particles.splice(0, particles.length - performanceConfig.maxParticles);
      }

      // 更新和绘制粒子
      for (let i = particles.length - 1; i >= 0; i--) {
        if (!particles[i].update()) {
          particles.splice(i, 1);
        } else {
          particles[i].draw();
        }
      }

      animationId = requestAnimationFrame(animate);
    }

    // 显示祝福语
    setTimeout(() => setShowMessage(true), 1000);

    animate(0);

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    // 点击/触摸发射烟花
    const handleInteraction = (e) => {
      e.preventDefault();
      
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      
      // 移动端发射2个，桌面发射3个
      const count = isMobile ? 2 : 3;
      for (let i = 0; i < count; i++) {
        setTimeout(() => {
          fireworks.push(new Firework(x, y));
        }, i * 150);
      }
    };

    window.addEventListener('resize', handleResize);
    canvas.addEventListener('click', handleInteraction);
    canvas.addEventListener('touchstart', handleInteraction, { passive: false });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('click', handleInteraction);
      canvas.removeEventListener('touchstart', handleInteraction);
      cancelAnimationFrame(animationId);
    };
  }, [started, isMobile]);

  // 10秒后显示特别祝福
  useEffect(() => {
    if (started) {
      const wishTimer = setTimeout(() => {
        setShowSpecialWish(true);
      }, 10000);
      
      return () => clearTimeout(wishTimer);
    }
  }, [started]);

  // 启动按钮点击
  const handleStart = () => {
    setStarted(true);
  };

  return (
    <div className="App">
      <canvas ref={canvasRef} className="fireworks-canvas" />
      
      <div className={`content ${showMessage ? 'show' : ''}`}>
        <div className="title">新年快乐</div>
        <div className="subtitle">Happy New Year 2026</div>
        <div className="message">愿新的一年，万事如意，心想事成</div>
        {started && <div className="hint">点击屏幕发射烟花 ✨</div>}
      </div>

      <div className="decorations">
        <div className="lantern lantern-left"></div>
        <div className="lantern lantern-right"></div>
      </div>

      {/* 特别祝福 */}
      {showSpecialWish && (
        <div className="special-wish-overlay" onClick={() => setShowSpecialWish(false)}>
          <div className="special-wish-card">
            <div className="wish-title">✨ 特别的祝福 ✨</div>
            <div className="wish-content">
              <p className="wish-name">愿杜欣同学</p>
              <p className="wish-line">永远开开心心</p>
              <p className="wish-line">眼里有光，心中有爱</p>
              <p className="wish-line">所求皆所愿，所行皆坦途</p>
              <p className="wish-line last">日子闪闪发光 ✨</p>
            </div>
            <div className="wish-footer">点击任意位置继续</div>
          </div>
        </div>
      )}

      {!started && (
        <div className="audio-prompt" onClick={handleStart}>
          <div className="audio-prompt-content">
            <div className="audio-icon">🎆</div>
            <p>点击开始烟花表演</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
